import { EventCategory, GetIdeaResponse } from '@orbit/api-types'
import { BOOKMARK_CATEGORIES, BOOKMARK_CATEGORY_LABELS } from './utils'

/**
 * The filterable location label: locality, or the full free-text for a custom
 * location (the wire sends `locality` as `""` there). Mirrors iOS so both
 * clients group ideas identically.
 */
const locationLabel = (idea: GetIdeaResponse): string | null => {
	if (!idea.location) return null
	return idea.location.locality || idea.location.fullText
}

/** Unique location labels for the filter dropdown; shortest first (as iOS does). */
export const getLocationOptions = (ideas: GetIdeaResponse[]): string[] => {
	const labels = ideas
		.map(locationLabel)
		.filter((label): label is string => label !== null)
	return [...new Set(labels)].sort(
		(a, b) => a.length - b.length || a.localeCompare(b),
	)
}

/** Categories that have ideas, in the canonical category order, with labels. */
export const getCategoryOptions = (
	ideas: GetIdeaResponse[],
): { value: EventCategory; label: string }[] => {
	const present = new Set(ideas.map(idea => idea.category))
	return BOOKMARK_CATEGORIES.filter(category => present.has(category)).map(
		category => ({ value: category, label: BOOKMARK_CATEGORY_LABELS[category] }),
	)
}

export interface IdeaFilters {
	location: string | null
	category: EventCategory | null
}

/**
 * Ideas matching every active filter (`null` means "all", same as iOS): the
 * location label must equal the selection — so ideas without a location never
 * match an active location filter — and the category must equal the selection.
 */
export const filterIdeas = (
	ideas: GetIdeaResponse[],
	{ location, category }: IdeaFilters,
): GetIdeaResponse[] =>
	ideas.filter(
		idea =>
			(location === null || locationLabel(idea) === location) &&
			(category === null || idea.category === category),
	)

/**
 * The idea's own picture: the server-derived entity thumbnail (a link preview,
 * or a photo of its location when it has no links). Rows written before that
 * field existed fall back to the first link carrying a thumbnail; `null` after
 * both means the tile shows its icon instead.
 */
export const ideaThumbnailUrl = (idea: GetIdeaResponse): string | null =>
	idea.thumbnail || (idea.links.find(link => link.thumbnail)?.thumbnail ?? null)
